import React, { useEffect, useRef, useState } from "react";
import { filterPriceInput, normalizePriceInput } from "./productListPriceInput";

type ProductListPriceCellProps = {
  sellPrice?: string;
  costPrice?: string;
  disabled?: boolean;
  explicitSave?: boolean;
  onCommit: (sellPrice: string, costPrice: string) => void | Promise<void>;
};

const ProductListPriceCell: React.FC<ProductListPriceCellProps> = ({
  sellPrice,
  costPrice,
  disabled,
  explicitSave = false,
  onCommit,
}) => {
  const [sales, setSales] = useState(String(sellPrice ?? ""));
  const [cost, setCost] = useState(String(costPrice ?? ""));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);
  const lock = useRef(false);

  useEffect(() => {
    setSales(String(sellPrice ?? ""));
  }, [sellPrice]);
  useEffect(() => {
    setCost(String(costPrice ?? ""));
  }, [costPrice]);

  const commit = async () => {
    if (disabled || lock.current) {
      return;
    }
    const strict = (raw: string) => {
      if (raw.trim() === ".") return null;
      const value = raw.trim().replace(/,/g, "").replace(/\.$/, "");
      return value === "" || (/^\d+(\.\d+)?$/.test(value) && Number.isFinite(Number(value))) ? value : null;
    };
    const nextSales = explicitSave ? strict(sales) : normalizePriceInput(sales);
    const nextCost = explicitSave ? strict(cost) : normalizePriceInput(cost);
    if (nextSales === null || nextCost === null) {
      if (explicitSave) { setError("Enter a valid non-negative price, or leave it blank to clear it."); return; }
      setSales(String(sellPrice ?? ""));
      setCost(String(costPrice ?? ""));
      return;
    }
    setSales(nextSales);
    setCost(nextCost);
    if (nextSales === String(sellPrice ?? "") && nextCost === String(costPrice ?? "")) {
      return;
    }
    if (!explicitSave) {
      void onCommit(nextSales, nextCost);
      return;
    }
    lock.current = true; setSaving(true); setError(""); setSaved(false);
    try {
      await onCommit(nextSales, nextCost);
      setSaved(true);
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : "Prices weren’t saved. Please try again.");
    } finally { lock.current = false; setSaving(false); }
  };

  return (
    <div className={`product-list-prices${explicitSave ? " product-list-prices--inventory" : ""}`} onClick={(event) => event.stopPropagation()}>
      <label className="product-list-prices__row">
        <span>{explicitSave ? "Selling Price" : "Sales"}</span>
        <input
          type="text"
          inputMode="decimal"
          aria-label={explicitSave ? "Selling Price" : "Sales price"}
          placeholder="Not priced"
          value={sales}
          disabled={disabled || saving}
          onChange={(event) => { setSaved(false); setSales(explicitSave ? event.target.value : filterPriceInput(event.target.value)); }}
          onBlur={explicitSave ? undefined : () => void commit()}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              if (explicitSave) { event.preventDefault(); void commit(); return; }
              (event.target as HTMLInputElement).blur();
            }
          }}
        />
      </label>
      <label className="product-list-prices__row">
        <span>{explicitSave ? "Cost Price" : "Cost"}</span>
        <input
          type="text"
          inputMode="decimal"
          aria-label="Cost price"
          placeholder="Not entered"
          value={cost}
          disabled={disabled || saving}
          onChange={(event) => { setSaved(false); setCost(explicitSave ? event.target.value : filterPriceInput(event.target.value)); }}
          onBlur={explicitSave ? undefined : () => void commit()}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              if (explicitSave) { event.preventDefault(); void commit(); return; }
              (event.target as HTMLInputElement).blur();
            }
          }}
        />
      </label>
      {explicitSave && <button type="button" className="btn btn-hastkala-outline" disabled={disabled || saving || (sales === String(sellPrice ?? "") && cost === String(costPrice ?? ""))} onClick={() => void commit()}>{saving ? "Saving…" : "Save prices"}</button>}
      {error && <small role="alert">{error}</small>}
      {explicitSave && saved && <small role="status">Saved</small>}
    </div>
  );
};

export default ProductListPriceCell;
